import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './BuyerContactSelect.css';

/**
 * BuyerContactSelect Component
 * Renders radio buttons to select a contact from the client's contacts array.
 * When allowFreeText is true (Publico General RFC), also renders a
 * free-text input for the buyer name. The "+ Agregar contacto" button is
 * always available (regardless of allowFreeText or existing contacts) and
 * persists a real contact to the client via onAddContact.
 */
const BuyerContactSelect = ({
  contacts = [],
  selectedContact = null,
  onSelect = () => {},
  onAddContact = () => {},
  disabled = false,
  allowFreeText = false
}) => {
  const { t } = useTranslation();
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '' });

  const handleContactSelect = (contact, index) => {
    onSelect({
      id: index,
      name: contact.name || '',
      phone: contact.phone || '',
      email: contact.email || ''
    });
  };

  const handleFreeTextChange = (e) => {
    onSelect({ id: null, name: e.target.value, phone: '', email: '' });
  };

  const handleNewContactFieldChange = (e) => {
    const { name, value } = e.target;
    setNewContact((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddContactSubmit = () => {
    onAddContact({
      name: newContact.name.trim(),
      phone: newContact.phone.trim(),
      email: newContact.email.trim()
    });
    setNewContact({ name: '', phone: '', email: '' });
    setShowAddContact(false);
  };

  const freeTextInput = (
    <div className="form-group">
      <label className="form-label" htmlFor="buyer-free-text-input">
        {t('clientQuotes.buyerFreeTextLabel')}
      </label>
      <input
        id="buyer-free-text-input"
        type="text"
        className="form-input"
        value={selectedContact?.name || ''}
        onChange={handleFreeTextChange}
        placeholder={t('clientQuotes.buyerFreeTextPlaceholder')}
        disabled={disabled}
      />
    </div>
  );

  const addContactControl = (
    <div className="mt-10">
      <button
        type="button"
        className="btn btn-secondary btn-small"
        onClick={() => setShowAddContact((prev) => !prev)}
      >
        {t('clientQuotes.addContactButton')}
      </button>

      {showAddContact && (
        <div className="add-contact-form mt-10">
          <div className="form-group">
            <label className="form-label">{t('clients.contactName')}</label>
            <input
              type="text"
              name="name"
              value={newContact.name}
              onChange={handleNewContactFieldChange}
              placeholder={t('clients.contactName')}
              className="form-input"
            />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">{t('clients.contactPhone')}</label>
              <input
                type="tel"
                name="phone"
                value={newContact.phone}
                onChange={handleNewContactFieldChange}
                placeholder={t('clients.contactPhone')}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('clients.contactEmail')}</label>
              <input
                type="email"
                name="email"
                value={newContact.email}
                onChange={handleNewContactFieldChange}
                placeholder={t('clients.contactEmail')}
                className="form-input"
              />
            </div>
          </div>
          <button type="button" onClick={handleAddContactSubmit} className="btn btn-primary">
            {t('clients.addContact')}
          </button>
        </div>
      )}
    </div>
  );

  if (!contacts || contacts.length === 0) {
    if (allowFreeText) {
      return (
        <div className="buyer-contact-select">
          {freeTextInput}
          {addContactControl}
        </div>
      );
    }

    return (
      <div className="buyer-contact-select empty">
        <div className="empty-message">
          <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <p>{t('clientQuotes.noContactsAvailable')}</p>
        </div>
        {addContactControl}
      </div>
    );
  }

  return (
    <div className="buyer-contact-select">
      <fieldset className="contact-fieldset">
        <legend className="contact-legend">{t('table.buyer')}</legend>

        <div className="contact-options">
          {contacts.map((contact, index) => {
            const isSelected = selectedContact?.name === contact.name;
            const contactId = `contact-${index}`;

            return (
              <label
                key={index}
                className={`contact-option ${isSelected ? 'selected' : ''}`}
                htmlFor={contactId}
              >
                <input
                  id={contactId}
                  type="radio"
                  name="buyer_contact"
                  value={contact.name}
                  checked={isSelected}
                  onChange={() => handleContactSelect(contact, index)}
                  disabled={disabled}
                  className="contact-radio"
                  aria-label={`Select ${contact.name} as buyer contact`}
                />

                <span className="contact-info">
                  <span className="contact-name">
                    {contact.name}
                  </span>

                  {contact.phone && (
                    <span className="contact-phone">
                      <svg className="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      {contact.phone}
                    </span>
                  )}

                  {contact.email && (
                    <span className="contact-email">
                      <svg className="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      {contact.email}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {allowFreeText && freeTextInput}
      {addContactControl}
    </div>
  );
};

export default BuyerContactSelect;
